import { computed, ref } from 'vue';
import { useAuthStore } from '~/stores/useAuthStore';
import { useMenuStore } from '~/stores/useMenuStore';
import { showAlert } from '~/composables/useConfirmDialog';
import { appT } from '~/composables/useAppI18n';
import { useUserApi } from './useUserApi';
import { normalizeAppLocale, resolveAppLocale, setAppLocale, syncAppLocaleRoute } from '~/composables/useAppLocale';

/** 메뉴 path 비교용 정규화 */
const normalizeMenuPath = (path: string): string => {
  if (!path) return '';
  const trimmed = path.trim().replace(/\/+$/, '');
  return trimmed.replace(/^\/screens(?=\/|$)/, '') || trimmed;
};

const menuPathsMatch = (menuPath: string, targetPath: string): boolean => {
  if (!menuPath || !targetPath) return false;
  return normalizeMenuPath(menuPath) === normalizeMenuPath(targetPath);
};

/** 메뉴 트리에서 path에 해당하는 표시명 조회 (섹션/메뉴명 형식) */
const findMenuDisplayNameByPath = (sections: any[], targetPath: string): string | null => {
  for (const section of sections) {
    if (section.submenus) {
      for (const submenu of section.submenus) {
        if (submenu.items) {
          for (const item of submenu.items) {
            if (menuPathsMatch(item.path, targetPath)) {
              const sectionTitle = section.title || '';
              const itemName = item.name || submenu.title || '';
              if (sectionTitle && itemName && sectionTitle !== itemName) {
                return `${sectionTitle}/${itemName}`;
              }
              return itemName || sectionTitle || null;
            }

            if (item.tabs) {
              for (const tab of item.tabs) {
                if (menuPathsMatch(tab.path, targetPath)) {
                  const sectionTitle = section.title || '';
                  const itemName = item.name || submenu.title || '';
                  if (sectionTitle && itemName && sectionTitle !== itemName) {
                    return `${sectionTitle}/${itemName}`;
                  }
                  return itemName || sectionTitle || null;
                }
              }
            }
          }
        }
      }
    }

    if (section.items) {
      for (const item of section.items) {
        if (menuPathsMatch(item.path, targetPath)) {
          const sectionTitle = section.title || '';
          const itemName = item.name || '';
          if (sectionTitle && itemName && sectionTitle !== itemName) {
            return `${sectionTitle}/${itemName}`;
          }
          return itemName || sectionTitle || null;
        }
      }
    }
  }

  return null;
};

/**
 * 사용자 UI 상태 관리 컴포저블
 * 목적: 사용자 정보 및 설정 관리
 * 참고: TheHeader 모달 상태도 여기서 관리
 */

/****************************************************
 * 타입 정의
 ****************************************************/
interface ModalState {
  filter: boolean;
  notification: boolean;
  fullMenu: boolean;
  [key: string]: boolean;
}

/****************************************************
 * 상태 변수 선언
 ****************************************************/
// 일반 상태
const isMainPageChange = ref(false); // 메인페이지 변경 스위치 상태
const selectedMainPage = ref({
  path: '',
  name: '',
}); // 선택된 메인페이지 정보
const filterButton = ref<HTMLElement | null>(null); // 필터 버튼 참조
const userSessionInfo = ref<Record<string, unknown> | null>(null);
const isLoading = ref(false);
const error = ref<Error | null>(null);

// 사용자 설정
const userSettings = ref({
  mailReceiveYn: 'N',
  pushYnWeb: 'N',
  connectYn: 'N',
  aiSaiUseYn: 'N',
});

const langList = ref<{ lang: string; langNm: string }[]>([]);
const userType = ref('');
const compSaiUseYn = ref('N');
/** selectUserInfo.do → userInfo AI 기능 사용 여부 (세션 loginVO와 동일 출처) */
const aiKpiOkrCreateUseYn = ref('N');
const aiFeedbackCreateUseYn = ref('N');
const aiEditorHelperUseYn = ref('N');
const aiPlanTemplateUseYn = ref('N');
/** AI유형 05 (v_com_code CODE_GRP_ID=464) — AI 성과기술서 생성 */
const aiPerfStmtCreateUseYn = ref('N');
/** AI유형 06 — 인사평가 실적근거 요약 (성과평가의 'AI 실적근거 요약' 버튼 표시 여부) */
const aiEvidenceSummaryUseYn = ref('N');
const selectedLang = ref('ko');
const slackUserConnectYn = ref('N');

/** AI Helper 토큰 현황 (잔여 한도 프로그레스 바용) */
const userAiTokenStatus = ref({
  usedToken: 0,
  limitToken: 0,
  remainToken: 0,
  usageRate: 0,
});

// 베타 기능 플래그 (사용자별 베타 기능 활성화)
const betaFeatures = ref({
  sai: false, // SAI 챗봇 기능
  pa: false, // PA 메뉴 기능
});

/****************************************************
 * TheHeader 모달 상태 관리 (통합 패턴)
 ****************************************************/
// 모든 모달 상태를 단일 객체로 관리
const modals: ModalState = reactive({
  filter: false, // 사용자 정보 필터 모달
  notification: false, // 알림 모달
  fullMenu: false, // 전체 메뉴 모달
});

/****************************************************
 * 함수 선언
 ****************************************************/

const toYn = (value: unknown) => {
  const normalized = String(value ?? 'N').trim();
  return normalized || 'N';
};

/** CommonController.selectUserInfo — 세션 AI 플래그를 userInfo에 merge한 값 동기화 */
const syncAiUseYnFromUserInfo = (userInfo: Record<string, unknown> | null | undefined) => {
  aiKpiOkrCreateUseYn.value = toYn(userInfo?.aiKpiOkrCreateUseYn);
  aiFeedbackCreateUseYn.value = toYn(userInfo?.aiFeedbackCreateUseYn);
  aiEditorHelperUseYn.value = toYn(userInfo?.aiEditorHelperUseYn);
  aiPlanTemplateUseYn.value = toYn(userInfo?.aiPlanTemplateUseYn);
  aiPerfStmtCreateUseYn.value = toYn(userInfo?.aiPerfStmtCreateUseYn);
  aiEvidenceSummaryUseYn.value = toYn(userInfo?.aiEvidenceSummaryUseYn);
};

// 사용자 정보 조회 API
const fetchUserInfo = async () => {
  try {
    isLoading.value = true;
    error.value = null;

    const response = await useApi('/api/common/selectUserInfo.do', {
      method: 'POST',
    });

    userSessionInfo.value = response.userInfo;
    userSettings.value.mailReceiveYn = response.userInfo?.mailReceiveYn || 'N';
    userSettings.value.pushYnWeb = response.userInfo?.pushYnWeb || 'N';
    userSettings.value.connectYn = response.userInfo?.slackYn || 'N';
    slackUserConnectYn.value = response.userInfo?.slackYn || 'N';
    userSettings.value.aiSaiUseYn = response.userInfo?.aiSaiUseYn || 'N';
    langList.value = response.langList || [];
    userType.value = response.userType || response.userInfo?.userType || '';
    compSaiUseYn.value = response.userInfo?.compSaiUseYn || 'N';
    syncAiUseYnFromUserInfo(response.userInfo);
    const resolvedLang = resolveAppLocale(response.userInfo?.lang);
    selectedLang.value = resolvedLang;

    if (import.meta.client) {
      await setAppLocale(resolvedLang);
      await syncAppLocaleRoute(resolvedLang);
    }

    // 베타 기능 플래그 설정
    betaFeatures.value.sai = response.userInfo?.aiSaiUseYn === 'Y';
    betaFeatures.value.pa = response.userInfo?.paUseYn === 'Y';

    return response.userInfo;
  } catch (err) {
    error.value = err instanceof Error ? err : new Error(String(err));
    console.error('사용자 정보 조회 실패:', err);
    throw err;
  } finally {
    isLoading.value = false;
  }
};

const init = async () => {
  await fetchUserInfo();
};

const resetUserAiTokenStatus = () => {
  userAiTokenStatus.value = {
    usedToken: 0,
    limitToken: 0,
    remainToken: 0,
    usageRate: 0,
  };
};

const isAnyAiHelperEnabled = () =>
  aiKpiOkrCreateUseYn.value === 'Y' ||
  aiFeedbackCreateUseYn.value === 'Y' ||
  aiEditorHelperUseYn.value === 'Y' ||
  aiPlanTemplateUseYn.value === 'Y' ||
  aiPerfStmtCreateUseYn.value === 'Y' ||
  aiEvidenceSummaryUseYn.value === 'Y';

const fetchUserAiTokenStatus = async () => {
  if (!isAnyAiHelperEnabled()) {
    resetUserAiTokenStatus();
    return;
  }

  try {
    const res = await useUserApi().fetchUserAiTokenStatus();
    const status = res?.tokenStatus ?? {};
    userAiTokenStatus.value = {
      usedToken: Number(status.usedToken ?? 0),
      limitToken: Number(status.limitToken ?? 0),
      remainToken: Number(status.remainToken ?? 0),
      usageRate: Number(status.usageRate ?? 0),
    };
  } catch (e) {
    console.error('AI 토큰 현황 조회 실패:', e);
    resetUserAiTokenStatus();
  }
};

/****************************************************
 * 모달 제어 함수
 ****************************************************/
function openModal(id: string) {
  modals[id] = true;
}

function closeModal(id: string) {
  modals[id] = false;
}

function toggleModal(id: string) {
  modals[id] = !modals[id];
}

function closeAllModals() {
  Object.keys(modals).forEach(k => (modals[k] = false));
}

/****************************************************
 * Export
 ****************************************************/
export const useUserStore = () => {
  // ✅ authStore는 함수 내부에서 호출 (Pinia 초기화 후)
  const authStore = useAuthStore();
  const menuStore = useMenuStore();
  const userApi = useUserApi();

  // ✅ selectedMainPage 초기화 (authStore 사용 가능)
  if (!selectedMainPage.value.path && authStore.user?.mainPgmPath) {
    selectedMainPage.value = {
      path: authStore.user.mainPgmPath,
      name: authStore.user.mainPgmNm || '',
    };
  }

  // 사용자 정보 - computed로 자동 반응형 업데이트
  const userInfo = computed(() => {
    // 로그인되지 않은 상태에서는 기본값 반환
    if (!authStore.user) {
      return {
        compId: '',
        name: '',
        id: '',
        department: '',
        position: '',
        profile: '/images/icon-gnb-my.svg',
        lang: 'ko',
      };
    }

    // authStore.user에 추가 속성이 있을 수 있으므로 타입 단언 사용
    const user = authStore.user as Record<string, unknown>;

    return {
      compId: authStore.user.compId,
      name: authStore.user.userNm,
      id: authStore.user.userId,
      department: (user.deptNm as string) || '',
      position: (user.posNm as string) || '',
      profile: (user.profile as string) || '/images/icon-gnb-my.svg',
      lang: (user.lang as string) || 'ko',
    };
  });

  /**
   * 이메일 알림 설정 변경 함수
   * @param {string} mailReceiveYn - 'Y' 또는 'N'
   */
  const changeMailSet = async () => {
    try {
      const params = {
        mailReceiveYn: userSettings.value.mailReceiveYn,
      };
      const res = await userApi.changeMailSet(params);
      return res;
    } catch (e) {
      console.error('이메일 알림 설정 변경 실패:', e);
      throw e;
    }
  };

  /**
   * 메인페이지 설정 함수
   * @param {string} path - 페이지 경로
   * @param {string} name - 페이지 이름
   */
  const setMainPage = async (path: string, name: string) => {
    /* 메인페이지 설정 함수 */
    selectedMainPage.value = { path, name };
    try {
      const params = {
        path: path,
      };
      const res = await userApi.updateMainMenu(params);
      console.log(`메인페이지가 "${name}"으로 설정되었습니다.`);
      return res;
    } catch (e) {
      console.error('메인페이지 변경 실패:', e);
      throw e;
    }
  };

  /**
   * 푸시 알림 설정 변경 함수
   * @param {string} pushYnWeb - 'Y' 또는 'N'
   */
  const changeNotifySet = async () => {
    try {
      const params = {
        pushYnWeb: userSettings.value.pushYnWeb,
      };
      const res = await userApi.changeNotifySet(params);
      return res;
    } catch (e) {
      console.error('푸시 알림 설정 변경 실패:', e);
      throw e;
    }
  };

  const langOptions = computed(() => {
    if (langList.value.length > 0) {
      return langList.value.map(item => ({
        value: item.lang,
        label: item.langNm,
      }));
    }
    return [
      { value: 'ko', label: '한글' },
      { value: 'en', label: 'ENG' },
    ];
  });

  const notifyWarning = computed(() => {
    if (!import.meta.client || typeof Notification === 'undefined') {
      return false;
    }
    return Notification.permission === 'denied';
  });

  /** SPA locale 변경 — URL ?lang= 유지, 서버 세션 동기화 */
  const changeLang = async (pgmId?: string) => {
    if (!import.meta.client) return;

    const lang = normalizeAppLocale(selectedLang.value);

    try {
      await setAppLocale(lang);

      const nuxtApp = useNuxtApp();
      const router = nuxtApp.$router;
      const route = router.currentRoute.value;

      await router.replace({
        path: route.path,
        query: { ...route.query, lang },
        hash: pgmId ? `#${pgmId}` : route.hash || undefined,
      });

      // 레거시: ?lang= 파라미터로 서버 세션 언어·메뉴 갱신 (CustomLocaleChangeInterceptor)
      await useApi('/api/common/selectUserInfo.do', {
        method: 'POST',
        query: { lang },
        skipLoading: true,
      });

      const { notifyHrEvalCalculationLocaleChanged } = await import(
        '~/composables/hr-evaluation/calculation/useHrEvalCalculationLocaleRefresh'
      );
      notifyHrEvalCalculationLocaleChanged();

      await menuStore.refreshMenuList({ userAuthorities: authStore.authorities });

      if (authStore.user) {
        (authStore.user as Record<string, unknown>).lang = lang;
      }

      const mainPath = selectedMainPage.value.path || authStore.user?.mainPgmPath;
      if (mainPath) {
        const mainName = findMenuDisplayNameByPath(menuStore.sections, mainPath);
        if (mainName) {
          selectedMainPage.value = { path: mainPath, name: mainName };
        }
      }

      const { useNotificationStore } = await import('~/composables/notifications/useNotificationStore');
      const notificationStore = useNotificationStore();
      notificationStore.resetNotifications();
      if (modals.notification) {
        await notificationStore.loadNotifications();
      }
    } catch (e) {
      console.error('언어 변경 실패:', e);
    }
  };

  const changeAiSaiUseYn = async () => {
    const aiSaiUseYn = userSettings.value.aiSaiUseYn;
    if (aiSaiUseYn === 'Y') {
      showAlert(appT('common.notice'), appT('common.userSettings.aiSaiEnabledNextLogin'));
    } else {
      showAlert(appT('common.notice'), appT('common.userSettings.aiSaiDisabledNextLogin'));
    }
    try {
      const res = await userApi.changeAiSaiUseYn({ aiSaiUseYn });
      betaFeatures.value.sai = aiSaiUseYn === 'Y';
      return res;
    } catch (e) {
      console.error('AI실험실(Sai) 설정 변경 실패:', e);
      throw e;
    }
  };

  const openSlackConnect = async () => {
    const connectYn = userSettings.value.connectYn;
    try {
      const res = await userApi.selectSlackUserConnectYn({ connectYn });
      const userConnectYn = res?.userConnectYn || 'N';
      slackUserConnectYn.value = userConnectYn;

      if ((connectYn === 'Y' && userConnectYn === 'Y') || (connectYn === 'N' && userConnectYn === 'N')) {
        return res;
      }

      if (connectYn === 'Y') {
        const runtimeConfig = useRuntimeConfig();
        const slackConnectUrl = res?.connectUrl || runtimeConfig.public.slackConnectUrl || '';
        const token = res?.token;
        if (slackConnectUrl && token) {
          const url = `${slackConnectUrl}&state=${token}`;
          window.open(url, 'slackAccessPop', 'width=600,height=800,top=100,left=200');
        }
      } else {
        const logoutRes = await userApi.slackLogout();
        userSettings.value.connectYn = logoutRes?.connectYn || 'N';
        slackUserConnectYn.value = logoutRes?.connectYn || 'N';
      }
      return res;
    } catch (e) {
      console.error('슬랙 연동 설정 변경 실패:', e);
      userSettings.value.connectYn = connectYn === 'Y' ? 'N' : 'Y';
      throw e;
    }
  };

  const showNotifyInfo = () => {
    showAlert(appT('common.notice'), appT('common.userSettings.pushNotificationDenied'));
  };

  const showAiTokenQuota = computed(() => isAnyAiHelperEnabled());

  const aiTokenRemainPercent = computed(() => {
    const { limitToken, remainToken } = userAiTokenStatus.value;
    if (!limitToken || limitToken <= 0) {
      return 0;
    }
    return Math.min(100, Math.max(0, (remainToken / limitToken) * 100));
  });

  const selectedMainPageDisplayName = computed(() => {
    const path = selectedMainPage.value.path || authStore.user?.mainPgmPath || '';
    if (path && menuStore.sections.length > 0) {
      const fromMenu = findMenuDisplayNameByPath(menuStore.sections, path);
      if (fromMenu) {
        return fromMenu;
      }
    }
    return selectedMainPage.value.name || authStore.user?.mainPgmNm || '';
  });

  return {
    // 상태
    isMainPageChange,
    selectedMainPage,
    selectedMainPageDisplayName,
    filterButton,
    setMainPage,

    // 배열
    userInfo,
    userSettings,
    langList,
    langOptions,
    userType,
    compSaiUseYn,
    aiKpiOkrCreateUseYn,
    aiFeedbackCreateUseYn,
    aiEditorHelperUseYn,
    aiPlanTemplateUseYn,
    aiPerfStmtCreateUseYn,
    aiEvidenceSummaryUseYn,
    userAiTokenStatus,
    showAiTokenQuota,
    aiTokenRemainPercent,
    selectedLang,
    notifyWarning,
    init,
    fetchUserAiTokenStatus,
    changeMailSet,
    changeNotifySet,
    changeLang,
    changeAiSaiUseYn,
    openSlackConnect,
    showNotifyInfo,

    // useUserSession에서 가져온 것들
    userSessionInfo,
    isLoading,
    error,
    fetchUserInfo,

    // 모달 관련
    modals,
    openModal,
    closeModal,
    toggleModal,
    closeAllModals,

    // 베타 기능
    betaFeatures,
  };
};
